import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu, X, Package } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { AnimatePresence, motion } from "framer-motion";

const LINKS = [
  { to: "/gifts", label: "All Gifts", testid: "nav-link-collections" },
  { to: "/gifts/watches", label: "Watches", testid: "nav-link-watches" },
  { to: "/gifts/dry-fruits", label: "Dry Fruits", testid: "nav-link-dry-fruits" },
  { to: "/gifts/gift-hampers", label: "Hampers", testid: "nav-link-hampers" },
  { to: "/gifts/personalized", label: "Personalized", testid: "nav-link-personalized" },
  { to: "/occasions", label: "Occasions", testid: "nav-link-occasions" },
  { to: "/festivals", label: "Festivals", testid: "nav-link-festivals" },
  { to: "/offers", label: "Offers", testid: "nav-link-offers" },
];

export default function Header() {
  const { cartCount, setCartOpen, user, cartPing } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearchOpen(false);
    setMobileOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <header data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${scrolled ? "glass-nav shadow-[0_8px_30px_rgba(0,0,0,0.45)]" : "bg-transparent border-b border-transparent"}`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between transition-[height] duration-500 ${scrolled ? "h-16" : "h-20"}`}>
        <button className="lg:hidden text-[#2E1A22] p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
          <Menu size={22} />
        </button>

        <Link to="/" data-testid="nav-logo" className="flex items-center select-none">
          <img src="/assets/savyron-logo.png" alt="SAVYRON — Gifts for Every Moment" className="h-10 sm:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid}
              className={({ isActive }) => `text-[13px] tracking-wide transition-colors duration-300 hover:text-[#7E1E3F] ${isActive ? "text-[#7E1E3F]" : "text-[#857078]"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={() => setSearchOpen(true)} className="p-2 text-[#857078] hover:text-[#7E1E3F] transition-colors" data-testid="nav-search-button" aria-label="Search">
            <Search size={19} />
          </button>
          <Link to="/track" className="hidden md:block p-2 text-[#857078] hover:text-[#7E1E3F] transition-colors" data-testid="nav-track-order-button" aria-label="Track order">
            <Package size={19} />
          </Link>
          <Link to={user ? "/account" : "/login"} className="p-2 text-[#857078] hover:text-[#7E1E3F] transition-colors" data-testid="nav-account-button" aria-label="Account">
            <User size={19} />
          </Link>
          <Link to="/account?tab=wishlist" className="hidden sm:block p-2 text-[#857078] hover:text-[#7E1E3F] transition-colors" data-testid="nav-wishlist-button" aria-label="Wishlist">
            <Heart size={19} />
          </Link>
          <button onClick={() => setCartOpen(true)} className="relative p-2 text-[#857078] hover:text-[#7E1E3F] transition-colors" data-testid="cart-drawer-trigger" aria-label="Cart">
            <motion.span key={cartPing} initial={{ scale: 1 }} animate={{ scale: [1, 1.25, 1] }} transition={{ duration: 0.4 }} className="block">
              <ShoppingBag size={19} />
            </motion.span>
            {cartCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#7E1E3F] text-[#FFFFFF] text-[10px] font-semibold w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {searchOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#FFFFFF]/95 backdrop-blur-xl flex items-start justify-center pt-32 px-4"
            onClick={() => setSearchOpen(false)} data-testid="search-overlay">
            <motion.form initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-2xl" onSubmit={submitSearch} onClick={(e) => e.stopPropagation()}>
              <p className="font-mono-lux text-[11px] uppercase tracking-[0.3em] text-[#7E1E3F] mb-4">Search Savyron</p>
              <div className="flex items-center gap-3 border-b border-[#7E1E3F]/40 pb-3">
                <Search className="text-[#857078]" size={22} />
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder='Try "birthday gift for father" or "diwali gift"'
                  className="w-full bg-transparent outline-none text-xl sm:text-2xl font-serif-lux text-[#2E1A22] placeholder:text-[#857078]"
                  data-testid="search-input" />
                <button type="button" onClick={() => setSearchOpen(false)} className="text-[#857078] hover:text-[#2E1A22]" data-testid="search-close-button"><X size={20} /></button>
              </div>
            </motion.form>
          </motion.div>
        )}
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] lg:hidden">
            <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-0 bottom-0 w-[84%] max-w-sm bg-[#FBF7F1] border-r border-[#7E1E3F]/20 p-6 overflow-y-auto" data-testid="mobile-drawer">
              <div className="flex items-center justify-between mb-8">
                <img src="/assets/savyron-logo.png" alt="SAVYRON" className="h-11 w-auto" />
                <button onClick={() => setMobileOpen(false)} className="text-[#857078]" data-testid="mobile-menu-close"><X size={22} /></button>
              </div>
              <div className="flex flex-col gap-5">
                {LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-lg font-serif-lux text-[#2E1A22]" data-testid={`mobile-${l.testid}`}>{l.label}</Link>
                ))}
                <Link to="/track" onClick={() => setMobileOpen(false)} className="text-lg font-serif-lux text-[#2E1A22]" data-testid="mobile-nav-track">Track Order</Link>
                <Link to="/corporate" onClick={() => setMobileOpen(false)} className="text-lg font-serif-lux text-[#2E1A22]" data-testid="mobile-nav-corporate">Corporate Gifting</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-lg font-serif-lux text-[#2E1A22]" data-testid="mobile-nav-contact">Contact</Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
